import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

/**
 * resourcePersonLearningPathStore — learning-path (bundle) authoring slice.
 *
 *   GET    /api/resource-person/learning-paths
 *   GET    /api/resource-person/learning-paths/:id
 *   GET    /api/resource-person/learning-paths/:id/analytics
 *   POST   /api/resource-person/learning-paths
 *   POST   /api/resource-person/learning-paths/:id/courses  { courseId, position? }
 *   PATCH  /api/resource-person/learning-paths/:id
 *   PATCH  /api/resource-person/learning-paths/:id/courses/reorder  { courseIds: [] }
 *   PATCH  /api/resource-person/learning-paths/:id/submit
 *   PATCH  /api/resource-person/learning-paths/:id/unsubmit
 *   DELETE /api/resource-person/learning-paths/:id
 *   DELETE /api/resource-person/learning-paths/:id/courses/:courseId
 */

export const resourcePersonLearningPathInitialState = {
  learningPaths: [],
  learningPathsLoading: false,
  learningPathsError: null,
  learningPathsTotal: 0,
  learningPathsPages: 1,
  learningPathDetail: null,
  learningPathDetailLoading: false,
  learningPathDetailError: null,
  learningPathAnalytics: null,
  learningPathAnalyticsLoading: false,
  learningPathAnalyticsError: null,
  learningPathMutationLoading: false,
  learningPathMutationError: null,
};

function buildLpUrl(path, params) {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== "")
  ).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

function lpRequestOptions(method = "GET", body) {
  const token = authSession.getToken();
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method,
    headers,
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function requestLpApi(path, options = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const response = await safeFetch(buildLpUrl(path, options.params), lpRequestOptions(options.method, options.body));
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (text.trim().startsWith("<")) throw new Error(`Learning path API returned HTML (${response.status}).`);
      throw new Error(`Learning path API returned a non-JSON response (${response.status}).`);
    }
  }
  if (!response.ok) {
    const err = new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function lpPayloadOf(data) {
  return data?.data && typeof data.data === "object" && !Array.isArray(data.data)
    ? data.data
    : data?.learningPath ?? data ?? {};
}

function lpListOf(data, keys) {
  const payload = lpPayloadOf(data);
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return Array.isArray(payload) ? payload : [];
}

function lpPaginationOf(data, items) {
  const payload = lpPayloadOf(data);
  return {
    items,
    total: payload.total ?? payload.totalItems ?? items.length,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

export function createResourcePersonLearningPathActions(set, get) {
  const failMutation = (error) => {
    set({ learningPathMutationLoading: false, learningPathMutationError: error.message });
    return { success: false, error: error.message, status: error.status };
  };
  const mutate = async (path, method = "PATCH", body) => {
    set({ learningPathMutationLoading: true, learningPathMutationError: null });
    try {
      const data = await requestLpApi(path, { method, ...(body !== undefined ? { body } : {}) });
      set({ learningPathMutationLoading: false });
      return { success: true, learningPath: lpPayloadOf(data) };
    } catch (error) {
      return failMutation(error);
    }
  };

  return {
    fetchLearningPaths: async (params = {}) => {
      set({ learningPathsLoading: true, learningPathsError: null });
      try {
        const data = await requestLpApi("/api/resource-person/learning-paths", { params });
        const result = lpPaginationOf(data, lpListOf(data, ["learningPaths", "items", "data"]));
        set({
          learningPathsLoading: false,
          learningPaths: result.items,
          learningPathsTotal: result.total,
          learningPathsPages: result.totalPages,
        });
        return { success: true, ...result };
      } catch (error) {
        set({ learningPathsLoading: false, learningPathsError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    fetchLearningPathById: async (id) => {
      set({ learningPathDetailLoading: true, learningPathDetailError: null });
      try {
        const data = await requestLpApi(`/api/resource-person/learning-paths/${id}`);
        const detail = lpPayloadOf(data);
        set({ learningPathDetailLoading: false, learningPathDetail: detail });
        return { success: true, learningPath: detail };
      } catch (error) {
        set({ learningPathDetailLoading: false, learningPathDetailError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    createLearningPath: async (payload) =>
      mutate("/api/resource-person/learning-paths", "POST", payload),

    updateLearningPath: async (id, payload) =>
      mutate(`/api/resource-person/learning-paths/${id}`, "PATCH", payload),

    deleteLearningPath: async (id) =>
      mutate(`/api/resource-person/learning-paths/${id}`, "DELETE"),

    addCourseToPath: async (id, courseId, position) =>
      mutate(`/api/resource-person/learning-paths/${id}/courses`, "POST",
        position === undefined ? { courseId } : { courseId, position }),

    removeCourseFromPath: async (id, courseId) =>
      mutate(`/api/resource-person/learning-paths/${id}/courses/${courseId}`, "DELETE"),

    reorderPathCourses: async (id, courseIds) =>
      mutate(`/api/resource-person/learning-paths/${id}/courses/reorder`, "PATCH", { courseIds }),

    submitLearningPath: async (id) =>
      mutate(`/api/resource-person/learning-paths/${id}/submit`, "PATCH"),

    unsubmitLearningPath: async (id) =>
      mutate(`/api/resource-person/learning-paths/${id}/unsubmit`, "PATCH"),

    fetchLearningPathAnalytics: async (id) => {
      set({ learningPathAnalyticsLoading: true, learningPathAnalyticsError: null });
      try {
        const data = await requestLpApi(`/api/resource-person/learning-paths/${id}/analytics`);
        const analytics = lpPayloadOf(data);
        set({ learningPathAnalyticsLoading: false, learningPathAnalytics: analytics });
        return { success: true, analytics };
      } catch (error) {
        set({ learningPathAnalyticsLoading: false, learningPathAnalyticsError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },
  };
}

const resourcePersonLearningPathActions = createResourcePersonLearningPathActions;
export default resourcePersonLearningPathActions;
